import { useCallback, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Plus, Search } from 'lucide-react'
import { useUpsertFact } from '@/api/queries'
import { PageHeader } from '@/components/app/PageHeader'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { DataTable } from '@/components/ui/DataTable'
import { Input, Select } from '@/components/ui/Field'
import { EmptyState } from '@/components/ui/States'
import { useToast } from '@/components/ui/Toast'
import { BulkActionBar } from '@/components/review/BulkActionBar'
import { StatusTabs } from '@/components/review/StatusTabs'
import { useReviewCollection } from '@/components/review/useReviewCollection'
import { FactDrawer } from './FactDrawer'
import { StandardFactsList } from './StandardFactsList'
import { buildColumns } from './columns'
import type { PartnerFact } from '@/types'

const searchText = (f: PartnerFact) => `${f.label} ${f.value}`

const isStandard = (f: PartnerFact) => f.kind === 'standard'
const isAdditional = (f: PartnerFact) => f.kind === 'additional'

/**
 * Two sections over one entity type, so `useReviewCollection` is called twice
 * rather than once with a kind parameter — that keeps each section's counts
 * and selection independent (docs/06-component-refactor.md §5.4).
 *
 * This page does not use `ReviewSurface`: it has two cards with different
 * rules rather than one filtered collection, which is exactly the case the
 * shell deliberately does not try to cover.
 */
export function FactsPage() {
  const { clientId } = useParams()
  const upsert = useUpsertFact(clientId!)
  const toast = useToast()

  const [editing, setEditing] = useState<PartnerFact | 'new' | null>(null)

  const standard = useReviewCollection<PartnerFact>(clientId!, 'facts', {
    searchText,
    noun: 'fact',
    extraFilter: isStandard,
  })

  const additional = useReviewCollection<PartnerFact>(clientId!, 'facts', {
    searchText,
    noun: 'fact',
    extraFilter: isAdditional,
  })

  const columns = buildColumns({ onFavorite: additional.setFavoriteFor })

  const reviewFromDrawer = useCallback(
    (id: string, status: 'approved' | 'rejected') => {
      additional.setStatusFor(id, status)
      setEditing(null)
    },
    [additional],
  )

  /** Standard facts render every verdict inline, so they ignore the status tabs. */
  const standardRows = standard.all.filter(isStandard)

  return (
    <>
      <PageHeader
        title="Partner facts"
        description="Verifiable statements about this client. Only approved facts are retrievable by the agent — an unapproved number is worse than a missing one."
        actions={
          <Button variant="primary" onClick={() => setEditing('new')}>
            <Plus size={14} aria-hidden />
            Add fact
          </Button>
        }
      />

      <div className="space-y-5">
        <Card className="overflow-hidden">
          <CardHeader
            title="Standard facts"
            description="The fixed template is not defined yet, so these render as a generic labelled list. Swapping in the real template is a rendering change."
          />
          <StandardFactsList
            facts={standardRows}
            loading={standard.isLoading}
            onSave={(id, value) => {
              void upsert.mutateAsync({ id, value }).then(() => toast.push('Fact saved.'))
            }}
            onReview={standard.setStatusFor}
            onAdd={() => setEditing('new')}
          />
        </Card>

        {/* `overflow-clip`, not `-hidden`: see BulkActionBar. */}
        <Card className="overflow-clip">
          <CardHeader
            title="Additional facts"
            description="Identified by the extractor rather than the template. Full review vocabulary applies."
          />

          <div className="flex flex-wrap items-center gap-2 px-4 pt-3">
            <div className="relative">
              <Search
                size={13}
                aria-hidden
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint"
              />
              <Input
                value={additional.query}
                onChange={(e) => additional.setQuery(e.target.value)}
                placeholder="Search facts"
                aria-label="Search additional facts"
                className="h-8 w-56 pl-7 text-[13px]"
              />
            </div>
            <Select
              value={additional.tagFilter}
              onChange={(e) => additional.setTagFilter(e.target.value)}
              aria-label="Filter by tag"
              className="h-8 w-40 text-[13px]"
            >
              <option value="all">All tags</option>
              {additional.tagPool.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </div>

          <div className="px-4">
            <StatusTabs
              value={additional.status}
              counts={additional.counts}
              onChange={additional.setStatus}
            />
          </div>

          <DataTable
            rows={additional.rows}
            columns={columns}
            rowKey={(f) => f.id}
            loading={additional.isLoading}
            selected={additional.selected}
            onSelectedChange={additional.setSelected}
            onRowClick={(f) => setEditing(f)}
            rowClassName={(f) => (f.previouslyRejected ? 'bg-warn-50/40' : undefined)}
            empty={
              <EmptyState
                title={
                  additional.query || additional.tagFilter !== 'all'
                    ? 'No facts match these filters'
                    : `No ${additional.status === 'favorites' ? 'favorited' : additional.status} facts`
                }
                description={
                  additional.all.length === 0
                    ? 'The last crawl did not surface any additional facts. Add one manually if you know of a claim worth exposing.'
                    : undefined
                }
                action={
                  <Button size="sm" onClick={() => setEditing('new')}>
                    Add fact
                  </Button>
                }
              />
            }
          />

          <BulkActionBar
            count={additional.selected.size}
            noun="fact"
            tagPool={additional.tagPool}
            onClear={additional.clearSelection}
            onApprove={additional.bulk.approve}
            onReject={additional.bulk.reject}
            onAddTag={additional.bulk.addTag}
            onDelete={additional.bulk.requestDelete}
          />
        </Card>
      </div>

      <FactDrawer
        fact={editing}
        tagPool={additional.tagPool}
        onClose={() => setEditing(null)}
        onSave={async (patch) => {
          await upsert.mutateAsync(patch)
          setEditing(null)
          toast.push(patch.id ? 'Fact saved.' : 'Fact added.')
        }}
        onReview={reviewFromDrawer}
        onFavorite={additional.setFavoriteFor}
      />

      {standard.deleteDialog}
      {additional.deleteDialog}
    </>
  )
}
